import React, { useState } from 'react';
import Icon from './Icon';
import { fetchMetadata } from '../utils/metadataFetcher';

const SharingForm = ({ onChange, initialData, onReset }) => {
  const [formData, setFormData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newData = {
      ...formData,
      [name]: value
    };
    setFormData(newData);
    onChange(newData);
  };

  const handleUrlChange = (e) => {
    const { value } = e.target;
    const newData = {
      ...formData,
      postUrl: value
    };
    setFormData(newData);
    onChange(newData);
  };

  const handleUrlBlur = async (e) => {
    const { value } = e.target;
    console.log('URL blur triggered with value:', value);
    
    // Auto-fetch metadata if URL looks valid
    if (value && (value.startsWith('http://') || value.startsWith('https://'))) {
      console.log('Starting metadata fetch for:', value);
      setIsLoading(true);
      try {
        const metadata = await fetchMetadata(value);
        console.log('Metadata fetched:', metadata);
        const updatedData = {
          ...formData,
          title: metadata.title || formData.title,
          featuredImage: metadata.image || formData.featuredImage,
          excerpt: metadata.description || formData.excerpt,
          accentColor: metadata.accentColor || formData.accentColor,
          originalMetadata: metadata
        };
        setFormData(updatedData);
        onChange(updatedData);
      } catch (error) {
        console.error('Failed to fetch metadata:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      console.log('URL not valid for metadata fetch:', value);
    }
  };

  const handleUrlKeyPress = (e) => {
    console.log('Key pressed:', e.key);
    if (e.key === 'Enter') {
      console.log('Enter key pressed, triggering blur');
      handleUrlBlur(e);
    }
  };

  const handlePlatformChange = (platform) => {
    const newData = {
      ...formData,
      platforms: {
        ...formData.platforms,
        [platform]: !formData.platforms[platform]
      }
    };
    setFormData(newData);
    onChange(newData);
  };

  const handleAccentColorChange = (e) => {
    const newData = {
      ...formData,
      accentColor: e.target.value
    };
    setFormData(newData);
    onChange(newData);
  };


  const platforms = [
    { key: 'bluesky', label: 'Bluesky', icon: 'bluesky' },
    { key: 'threads', label: 'Threads', icon: 'threads' },
    { key: 'facebook', label: 'Facebook', icon: 'facebook' },
    { key: 'mastodon', label: 'Mastodon', icon: 'mastodon' },
    { key: 'reddit', label: 'Reddit', icon: 'reddit' },
    { key: 'linkedin', label: 'LinkedIn', icon: 'linkedin' },
    { key: 'email', label: 'Email', icon: 'email' }
  ];

  return (
    <div className="container">
      <div>
        <div className="form-group">
          <label htmlFor="postUrl">
            Post URL *
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
              <Icon name="bluesky" size={12} />
              <Icon name="threads" size={12} />
              <Icon name="facebook" size={12} />
              <Icon name="mastodon" size={12} />
              <Icon name="reddit" size={12} />
              <Icon name="linkedin" size={12} />
              <Icon name="email" size={12} />
            </div>
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type="url"
              id="postUrl"
              name="postUrl"
              value={formData.postUrl}
              onChange={handleUrlChange}
              onBlur={handleUrlBlur}
              onKeyPress={handleUrlKeyPress}
              placeholder="https://example.com/your-post"
              required
              style={{ paddingRight: isLoading ? '40px' : '10px' }}
            />
            {isLoading && (
              <div style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #667eea',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
          </div>
          <small style={{ color: '#666', fontSize: '0.9rem' }}>
            Press Enter or click away to fetch metadata from the URL
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="title">
            Title
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
              <Icon name="bluesky" size={12} />
              <Icon name="threads" size={12} />
              <Icon name="mastodon" size={12} />
              <Icon name="reddit" size={12} />
              <Icon name="email" size={12} />
            </div>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            placeholder="Your post title"
          />
        </div>

        <div className="form-group">
          <label htmlFor="excerpt">
            Excerpt
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
              <Icon name="bluesky" size={12} />
              <Icon name="threads" size={12} />
              <Icon name="mastodon" size={12} />
              <Icon name="email" size={12} />
            </div>
          </label>
          <textarea
            id="excerpt"
            name="excerpt"
            value={formData.excerpt}
            onChange={handleInputChange}
            placeholder="Brief description of your post..."
          />
        </div>

        <div className="form-group">
          <label>Accent Color</label>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '10px', 
            padding: '10px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            background: '#f9f9f9'
          }}>
            <div 
              className="color-swatch"
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: formData.accentColor,
                borderRadius: '4px',
                border: '1px solid #ccc',
                cursor: 'pointer',
                position: 'relative'
              }} 
              onClick={() => document.getElementById('accentColorPicker').click()}
              title="Click to change color"
            />
            <input
              type="color"
              id="accentColorPicker"
              value={formData.accentColor}
              onChange={handleAccentColorChange}
              style={{
                position: 'absolute',
                opacity: 0,
                pointerEvents: 'none'
              }}
            />
            <div style={{ flex: 1 }}>
              <span style={{ color: '#666', fontSize: '0.9rem' }}>
                {formData.accentColor}
                {formData.accentColor !== '#667eea' && ' (detected from site)'}
              </span>
              <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '2px' }}>
                Click the color swatch to change it
              </div>
            </div>
          </div>
        </div>

        <div className="platforms-section">
          <h3>Select Platforms</h3>
          <div className="platforms-grid">
            {platforms.map(platform => (
              <label key={platform.key} className="platform-checkbox">
                <input
                  type="checkbox"
                  checked={formData.platforms[platform.key]}
                  onChange={() => handlePlatformChange(platform.key)}
                />
                <Icon name={platform.icon} size={20} />
                <span>{platform.label}</span>
              </label>
            ))}
          </div>
        </div>

        <button onClick={onReset} className="submit-button" style={{ background: '#6c757d' }}>
          Reset Form
        </button>
      </div>
    </div>
  );
};

export default SharingForm;
